import { Alert, Button, Flex, Form, Input, Select, Space, Switch } from 'antd';
import { FC, useCallback, useState } from 'react';
import { CloudCheckFill, CloudSlashFill } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps, ArrayElement, BaseQueryParams } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { DnsRecordsAllAutocomplete, useAutocompleteDnsRecordsQuery } from '@/modules/DnsRecords';
import {
  DomainConfigFormFields,
  DomainConfigFormInputs,
  DomainSchemeRedirect,
  DomainWWWRedirect,
  DomainsSchemeRedirectTag,
  DomainsWWWRedirectTag,
} from '@/modules/Domains';
import { DomainsApiProviders } from '@/modules/DomainsApi';
import { ProxyServersAddressesAutocomplete, ProxyServersAllAutocomplete } from '@/modules/ProxyServers';
import { SitesAllAutocomplete } from '@/modules/Sites';

interface Props extends AppFormProps<typeof DomainConfigFormInputs, DomainConfigFormFields> {
  proxyServerIdFormValue: string | undefined;
  dnsRecordIdFormValue: string | undefined;
  domainsCount?: number;
}

export const DomainsConfigFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  formErrors,
  isFormLoading,
  validationSchema,
  proxyServerIdFormValue,
  dnsRecordIdFormValue,
  domainsCount,
  handleBack,
  handleFinish,
  handleFinishFailed,
}) => {
  const [currentDnsRecordProvider, setCurrentDnsRecordProvider] = useState<DomainsApiProviders | null>(null);

  const handleSetCurrentDnsRecordProvider = (
    value: ArrayElement<BaseQueryParams<typeof useAutocompleteDnsRecordsQuery>['data']>,
  ) => {
    setCurrentDnsRecordProvider(value?.apiProvider ?? null);
  };

  return (
    <Form
      form={form}
      name={formName}
      initialValues={initialValues}
      autoComplete="off"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      disabled={isFormLoading}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <Form.Item
        label="Сайт"
        name={DomainConfigFormInputs.SiteId}
        hasFeedback
        required
        help={formErrors?.siteId}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.siteId)}
        rules={validationSchema.siteId}
      >
        <SitesAllAutocomplete disabled={isFormLoading} />
      </Form.Item>

      <Form.Item
        label="HTTPS"
        labelCol={{ span: 'auto' }}
        wrapperCol={{ span: 'auto' }}
        name={DomainConfigFormInputs.IsHttps}
        hasFeedback
        required
        help={formErrors?.isHttps}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.isHttps)}
        rules={validationSchema.isHttps}
        valuePropName="checked"
      >
        <Switch disabled={isFormLoading} checkedChildren="Увімкнено" unCheckedChildren="Вимкнено" />
      </Form.Item>

      <Form.Item
        label="Префікс статики"
        name={DomainConfigFormInputs.StaticPrefix}
        hasFeedback
        help={formErrors?.staticPrefix}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.staticPrefix)}
        rules={validationSchema.staticPrefix}
        extra="Можна залишити пустим"
        shouldUpdate
      >
        <Input disabled={isFormLoading} allowClear placeholder="Вкажіть префікс статики" />
      </Form.Item>

      <Form.Item
        label="WWW перенаправлення"
        name={DomainConfigFormInputs.WwwRedirect}
        hasFeedback
        required
        help={formErrors?.wwwRedirect}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.wwwRedirect)}
        rules={validationSchema.wwwRedirect}
      >
        <Select allowClear disabled={isFormLoading} placeholder="Вкажіть WWW перенаправлення">
          {Object.values(DomainWWWRedirect).map((redir) => (
            <Select.Option key={redir} value={redir}>
              <DomainsWWWRedirectTag redirect={redir} />
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Перенаправлення по схемі"
        name={DomainConfigFormInputs.SchemeRedirect}
        hasFeedback
        required
        help={formErrors?.schemeRedirect}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.schemeRedirect)}
        rules={validationSchema.schemeRedirect}
      >
        <Select placeholder="Вкажіть перенаправлення по схемі" allowClear disabled={isFormLoading}>
          {Object.values(DomainSchemeRedirect).map((redir) => (
            <Select.Option key={redir} value={redir}>
              <DomainsSchemeRedirectTag redirect={redir} />
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Проксі-сервер"
        name={DomainConfigFormInputs.ProxyServerId}
        hasFeedback
        required
        help={formErrors?.proxyServerId}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.proxyServerId)}
        rules={validationSchema.proxyServerId}
      >
        <ProxyServersAllAutocomplete />
      </Form.Item>

      <Form.Item
        label="Адреса проксі-сервера"
        name={DomainConfigFormInputs.ProxyServerAddressId}
        hasFeedback
        required
        help={formErrors?.proxyServerAddressId}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.proxyServerAddressId)}
        rules={validationSchema.proxyServerAddressId}
      >
        <ProxyServersAddressesAutocomplete proxyServerId={proxyServerIdFormValue} disabled={isFormLoading} />
      </Form.Item>

      <Form.Item
        label="DNS-запис"
        name={DomainConfigFormInputs.DnsRecordId}
        hasFeedback
        required
        help={formErrors?.dnsRecordId}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.dnsRecordId)}
        rules={validationSchema.dnsRecordId}
      >
        <DnsRecordsAllAutocomplete disabled={isFormLoading} onSelectValue={handleSetCurrentDnsRecordProvider} />
      </Form.Item>

      {currentDnsRecordProvider && (
        <>
          <Alert
            type="success"
            message="Після налаштування IP-адреса буде автоматично змінена"
            showIcon
            icon={<CloudCheckFill size={16} />}
            style={{ marginTop: -12 }}
          />
          <br />
        </>
      )}

      {dnsRecordIdFormValue && !currentDnsRecordProvider && (
        <>
          <Alert
            type="warning"
            message="DNS-запис не підтримує автоматичну зміну IP-адреси"
            showIcon
            icon={<CloudSlashFill size={16} />}
            style={{ marginTop: -12 }}
          />
          <br />
        </>
      )}

      <Form.Item wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
        <Flex gap={8} align="center" justify="space-between">
          <span>
            {domainsCount && 'Доменів обрано:'}
            &nbsp;
            {domainsCount && <strong>{domainsCount}</strong>}
          </span>

          <Space>
            <Button disabled={isFormLoading} type="default" onClick={handleBack}>
              Назад
            </Button>
            <ButtonSuccess loading={isFormLoading} type="primary" htmlType="submit">
              {formType === 'edit' ? 'Зберегти' : 'Сконфігурувати'}
            </ButtonSuccess>
          </Space>
        </Flex>
      </Form.Item>
    </Form>
  );
};
